import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "sonner";
import { useQueryClient, useMutation } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { clienteFormSchema, ClienteFormValues } from "../components/ClienteFormSchema";
import { Cliente } from "../types/cliente";
import { ClientesService } from "../services/clientes.service";
import { useAuth } from "@/features/auth";
import { useAutoPreenchimento } from "@/hooks/useAutoPreenchimento";
import { useProfileSwitcher } from "@/hooks/useProfileSwitcher";

export const useClienteFormSimplificado = (formData?: Cliente, onSuccess?: (cliente: Cliente) => void) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { profile } = useAuth();
  const { getCurrentProfile } = useProfileSwitcher();
  const { buscarPorCPF, dadosCliente, loading: loadingCPF } = useAutoPreenchimento();

  const currentRole = getCurrentProfile;

  const form = useForm<ClienteFormValues>({
    resolver: zodResolver(clienteFormSchema),
    defaultValues: {
      nome: formData?.nome || "",
      email: formData?.email || "",
      tel1: formData?.tel1 || "",
      tel2: formData?.tel2 || "",
      cpf: formData?.cpf || "",
      // Campos de Qualificação
      temEntrada: formData?.tem_entrada || false,
      tipoBem: formData?.tipoBem as any || "IMOVEL",
      valorEntrada: formData?.valor_entrada ? Number(formData.valor_entrada) : undefined,
      valorParcelaOrcamento: formData?.valor_parcela_orcamento ? Number(formData.valor_parcela_orcamento) : undefined,
      tempoProcura: formData?.tempo_procura as any || undefined,
      prioridade: formData?.prioridade as any || undefined,
      tempoAluguel: formData?.tempo_aluguel || "",
      statusAtual: formData?.status_atual || "",
    },
    mode: "onChange",
  });

  // Atualizar valores do formulário quando formData muda (para edição)
  useEffect(() => {
    if (formData) {
      form.reset({
        nome: formData.nome || "",
        email: formData.email || "",
        tel1: formData.tel1 || "",
        tel2: formData.tel2 || "",
        cpf: formData.cpf || "",
        // Campos de Qualificação
        temEntrada: formData.tem_entrada || false,
        tipoBem: formData.tipoBem as any || "IMOVEL",
        valorEntrada: formData.valor_entrada ? Number(formData.valor_entrada) : undefined,
        valorParcelaOrcamento: formData.valor_parcela_orcamento ? Number(formData.valor_parcela_orcamento) : undefined,
        tempoProcura: formData.tempo_procura as any || undefined,
        prioridade: formData.prioridade as any || undefined,
        tempoAluguel: formData.tempo_aluguel || "",
        statusAtual: formData.status_atual || "",
      });
    }
  }, [formData, form]);


  const { mutate: submitMutation, isPending: isSubmitting } = useMutation({
    mutationFn: async (data: ClienteFormValues) => {
      try {
        const tenantId = formData?.tenant_id || profile?.tenant_id;
        
        if (!tenantId || tenantId === null || tenantId === undefined || String(tenantId).trim() === '' || tenantId === 0) {
          throw new Error("Tenant ID não encontrado. Verifique se você está vinculado a uma empresa.");
        }

        const createdBy = profile?.uuid || profile?.id?.toString();
        if (!createdBy) {
          throw new Error("Usuário não identificado. Faça login novamente.");
        }

        const clienteData = {
          tenant_id: typeof tenantId === 'string' ? parseInt(tenantId) : tenantId,
          created_by: createdBy,
          nome: data.nome,
          email: data.email || undefined,
          tel1: data.tel1 || undefined,
          tel2: data.tel2 || undefined,
          cpf: data.cpf || undefined,
          // Campos obrigatórios com valores padrão
          nacionalidade: 'Brasileira',
          estado_civil: 'solteiro' as const,
          // Campos de Qualificação
          tem_entrada: data.temEntrada || false,
          tipo_bem: data.tipoBem || "IMOVEL",
          valor_entrada: data.valorEntrada ? data.valorEntrada.toString() : undefined,
          valor_parcela_orcamento: data.valorParcelaOrcamento ? data.valorParcelaOrcamento.toString() : undefined,
          tempo_procura: data.tempoProcura || undefined,
          prioridade: data.prioridade || undefined,
          tempo_aluguel: data.tempoAluguel || undefined,
          cliente_qualificado: data.temEntrada === true, // Cliente qualificado se tem entrada
          status_atual: data.statusAtual || undefined,
        };

        let savedCliente: Cliente;

        if (formData?.id) {
          // Não reenviar `status` em edições — evita derrubar um cliente já ativo de volta para rascunho
          savedCliente = await ClientesService.atualizarCliente(formData.id.toString(), clienteData);
        } else {
          savedCliente = await ClientesService.criarCliente({ ...clienteData, status: 'draft' as const });
        }

        return savedCliente;
      } catch (error: any) {
        console.error("Erro ao salvar lead qualificado:", error);
        throw error;
      }
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["clientes"] });
      queryClient.invalidateQueries({ queryKey: ["clientes-unified"] });
      
      toast.success(`Lead qualificado salvo com sucesso! Cliente: ${data.nome}`);
      
      if (onSuccess) {
        onSuccess(data);
      } else {
        navigate("/clientes");
      }
    },
    onError: (error: any) => {
      console.error("Erro ao salvar lead qualificado:", error);
      const errorMessage = error?.message || "Erro ao salvar lead qualificado. Tente novamente.";
      toast.error(errorMessage);
    },
  });

  const onSubmit = (data: ClienteFormValues) => {
    submitMutation(data);
  };

  const handleCPFSearch = async (cpf: string) => {
    if (!cpf || cpf.length < 11) return;
    await buscarPorCPF(cpf);
  };

  return {
    form,
    onSubmit,
    isSubmitting,
    handleCPFSearch,
    dadosCliente,
    loadingCPF,
    currentRole,
  };
};


